import {
  SeedlingDependenciesContextProvider as BaseSeedlingDependenciesContextProvider,
  FormSubmitted,
  SeedlingDependencies,
  UserClicked,
  UserInputted,
  UserViewed,
} from "@grow-therapy-team/seedling-components";
import { useFeatureGate } from "@statsig/react-bindings";
import { DevTools as JotaiDevTools, useAtomsDebugValue } from "jotai-devtools";
import { PropsWithChildren } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter } from "react-router-dom";
import { AuthedApolloProvider } from "./apollo";
import { Auth0ProviderWithNav as Auth0Provider } from "./auth";
import { logger } from "./datadog/logger";
import { ErrorPage } from "./error/ErrorPage";
import { Routes } from "./nav";
import { useBackForwardCacheReload } from "./nav/useBackForwardCacheReload";
import { useTrackEvent } from "./segment/segment";
import { Statsig, StatsigProvider } from "./statsig/StatsigProvider";
import { toast } from "./toasts";
import { Provider as JotaiProvider } from "jotai";
import { appJotaiStore } from "./state/jotai";

// Support for React Devtools
function DebugAtoms() {
  useAtomsDebugValue();
  return null;
}

const useFeatureFlag: NonNullable<
  SeedlingDependencies["statsig"]
>["useFeatureFlag"] = (flagName: string) => {
  const { value } = useFeatureGate(flagName);
  return {
    isLoading: false, // Feature gates are not loading in this context
    hasFeature: value,
  };
};

function SeedlingDependenciesContextProvider({ children }: PropsWithChildren) {
  const { formSubmitted, userClicked, userInputted, userViewed } =
    useTrackEvent();
  return (
    <BaseSeedlingDependenciesContextProvider
      value={{
        logger,
        segment: {
          userInputted: userInputted as UserInputted,
          formSubmitted: formSubmitted as FormSubmitted,
          userClicked: userClicked as UserClicked,
          userViewed: userViewed as UserViewed,
        },
        toast: {
          success: toast.success as NonNullable<
            SeedlingDependencies["toast"]
          >["success"],
          error: toast.error as NonNullable<
            SeedlingDependencies["toast"]
          >["error"],
        },
        statsig: {
          checkGate: Statsig.checkGate,
          useFeatureFlag,
        },
      }}
    >
      {children}
    </BaseSeedlingDependenciesContextProvider>
  );
}

function App() {
  useBackForwardCacheReload();

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <JotaiProvider store={appJotaiStore}>
        {/* Rest assured that the below dev tools are not available in the prod build */}
        <JotaiDevTools />
        <DebugAtoms />
        <StatsigProvider>
          <BrowserRouter>
            <Auth0Provider>
              <SeedlingDependenciesContextProvider>
                <AuthedApolloProvider>
                  <Routes />
                </AuthedApolloProvider>
              </SeedlingDependenciesContextProvider>
            </Auth0Provider>
          </BrowserRouter>
        </StatsigProvider>
      </JotaiProvider>
    </ErrorBoundary>
  );
}

export default App;
